/**
 * Mobile Vehicles List View
 * Shows fleet vehicles with compliance scores and alerts
 */

import React, { useState, useEffect } from 'react';
import { Gauge, AlertTriangle, ChevronRight, RefreshCw, Filter, Search } from 'lucide-react';
import { mobileAPI } from '../../api';

// Compliance score badge color
function getScoreColor(score) {
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// Status badge
function StatusBadge({ status }) {
  const colors = {
    active: 'bg-green-500/20 text-green-400',
    maintenance: 'bg-yellow-500/20 text-yellow-400',
    out_of_service: 'bg-red-500/20 text-red-400',
    retired: 'bg-slate-500/20 text-slate-400',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[status] || colors.active}`}>
      {status === 'active' ? 'Actif' : status === 'maintenance' ? 'Maintenance' : status}
    </span>
  );
}

export default function VehiclesView({ onSelect }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, alerts
  const [search, setSearch] = useState('');

  const loadVehicles = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const alertsOnly = filter === 'alerts' ? 1 : 0;
      const result = await mobileAPI.getVehicles({ alertsOnly });
      if (result.ok !== false) {
        setVehicles(result.items || []);
      }
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, [filter]);

  // Filter by search
  const filteredVehicles = vehicles.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      v.license_plate?.toLowerCase().includes(s) ||
      v.make?.toLowerCase().includes(s) ||
      v.model?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-white">Vehicules</h1>
          <button
            onClick={() => loadVehicles(true)}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-slate-700 text-slate-300'
            }`}
          >
            Tous ({vehicles.length})
          </button>
          <button
            onClick={() => setFilter('alerts')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
              filter === 'alerts'
                ? 'bg-red-500 text-white'
                : 'bg-slate-700 text-slate-300'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Alertes
          </button>
        </div>
      </header>

      {/* Vehicle List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Gauge className="w-12 h-12 mb-2 opacity-50" />
            <p>Aucun véhicule trouvé</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {filteredVehicles.map((vehicle) => (
              <li key={vehicle.id}>
                <button
                  onClick={() => onSelect(vehicle.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-800/50 active:bg-slate-800 transition-colors text-left"
                >
                  {/* Vehicle Icon with Score */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                      <Gauge className="w-6 h-6 text-slate-400" />
                    </div>
                    {/* Compliance Score Badge */}
                    <div
                      className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${getScoreColor(
                        vehicle.compliance_score
                      )}`}
                    >
                      {vehicle.compliance_score || 100}
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white truncate">
                        {vehicle.license_plate}
                      </span>
                      <StatusBadge status={vehicle.status} />
                    </div>
                    <p className="text-sm text-slate-400 truncate">
                      {vehicle.make} {vehicle.model}
                    </p>
                    <p className="text-xs text-slate-500">
                      {vehicle.mileage?.toLocaleString()} km
                    </p>
                  </div>

                  {/* Alert indicator + Arrow */}
                  <div className="flex items-center gap-2">
                    {(vehicle.open_todos > 0 || vehicle.compliance_score < 80) && (
                      <div className="flex items-center gap-1 text-amber-400">
                        <AlertTriangle className="w-4 h-4" />
                        {vehicle.open_todos > 0 && (
                          <span className="text-xs font-medium">{vehicle.open_todos}</span>
                        )}
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
